import { http } from "../http"

class SalesFetchApi {
    /**
     * Get sales data for a specific date range
     * @param {Object} options - Query parameters
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.warehouse_ids - Optional warehouse ID
     * @param {string} options.groupBy - How to group data (day, week, month)
     * @returns {Promise} Promise with sales data
     */
    static async getSalesOverTime(options = {}) {
        try {
            // Format the parameters to match the API requirements
            const params = {};

            // Map date parameters
            params.startDate = options.startDate;
            params.endDate = options.endDate;

            // Map grouping parameter
            params.interval = options.groupBy || 'day';

            // Add warehouse filtering if provided
            if (options.warehouse_ids) {
                params.warehouse_id = options.warehouse_ids;
            }

            // Add limit if needed
            if (options.limit) {
                params.limit = options.limit;
            }
            if (options.includePilotSales) {
                params.includePilotSales = "true";
            }

            const response = await http.get('/analytics/time-series-sales', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            return response.data;
        } catch (err) {
            console.error("Error fetching sales data:", err);
            throw err;
        }
    }

    /**
     * Get dashboard statistics for the given date range
     * @param {Object} options - Query parameters
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.prevStartDate - Previous period start date for comparison
     * @param {string} options.prevEndDate - Previous period end date for comparison
     * @returns {Promise} Promise with dashboard statistics
     */
    static async getDashboardStats(options = {}) {
        try {
            // Format the parameters to match the API requirements
            const params = {};

            // Set date parameters
            if (options.startDate) params.startDate = options.startDate;
            if (options.endDate) params.endDate = options.endDate;
            if (options.prevStartDate) params.prevStartDate = options.prevStartDate;
            if (options.prevEndDate) params.prevEndDate = options.prevEndDate;
            if (options.includePilotSales) params.includePilotSales = "true";

            const response = await http.get('/misc/dashboard-stats', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            return response.data;
        } catch (err) {
            console.error("Error fetching dashboard stats:", err);
            throw err;
        }
    }

    /**
     * Get sales data by warehouse for a specific date range
     * @param {Object} options - Query parameters
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @returns {Promise} Promise with sales by warehouse data
     */
    static async getSalesByWarehouse(options = {}) {
        try {
            // Format the parameters to match the API requirements
            const params = {};

            // Set date parameters
            if (options.startDate) params.startDate = options.startDate;
            if (options.endDate) params.endDate = options.endDate;
            if (options.includePilotSales) params.includePilotSales = "true";

            const response = await http.get('/analytics/sales-by-warehouse', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            return response.data;
        } catch (err) {
            console.error("Error fetching sales by warehouse data:", err);
            throw err;
        }
    }

    /**
     * Get sales data by product for a specific date range
     * @param {Object} options - Query parameters
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.warehouse_id - Optional warehouse ID for filtering
     * @param {number} options.limit - Optional limit for number of products (default: 20)
     * @returns {Promise} Promise with sales by product data
     */
    static async getSalesByProduct(options = {}) {
        try {
            // Format the parameters to match the API requirements
            const params = {};

            // Set date parameters
            if (options.startDate) params.startDate = options.startDate;
            if (options.endDate) params.endDate = options.endDate;

            // Add warehouse filtering if provided
            if (options.warehouse_id) {
                params.warehouse_id = options.warehouse_id;
            }

            // Add limit if provided
            if (options.limit) {
                params.limit = options.limit;
            }
            if (options.includePilotSales) {
                params.includePilotSales = "true";
            }

            const response = await http.get('/analytics/sales-by-product', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            return response.data;
        } catch (err) {
            console.error("Error fetching sales by product data:", err);
            throw err;
        }
    }

    /**
     * Get sale-time revenue grouped by the brand snapshot on invoice items.
     * @param {Object} options - Query parameters
     * @returns {Promise} Promise with sales by brand data
     */
    static async getSalesByBrand(options = {}) {
        try {
            const params = {};

            if (options.startDate) params.startDate = options.startDate;
            if (options.endDate) params.endDate = options.endDate;
            if (options.warehouse_id) params.warehouse_id = options.warehouse_id;
            if (options.limit) params.limit = options.limit;
            if (options.includePilotSales) params.includePilotSales = "true";

            const response = await http.get('/analytics/sales-by-brand', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            return response.data;
        } catch (err) {
            console.error("Error fetching sales by brand data:", err);
            throw err;
        }
    }

    /**
     * Get sales revenue grouped by product category for a specific date range
     * @param {Object} options - Query parameters
     * @param {string} options.startDate - Start date (YYYY-MM-DD)
     * @param {string} options.endDate - End date (YYYY-MM-DD)
     * @param {string} options.warehouse_id - Optional warehouse ID for filtering
     * @returns {Promise} Promise with category revenue data
     */
    static async getProductCategoryBreakdown(options = {}) {
        try {
            const params = {};

            if (options.startDate) params.startDate = options.startDate;
            if (options.endDate) params.endDate = options.endDate;
            if (options.includePilotSales) params.includePilotSales = "true";

            if (options.warehouse_id) {
                params.warehouse_id = options.warehouse_id;
            }

            const response = await http.get('/analytics/product-category-breakdown', {
                params,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            return response.data;
        } catch (err) {
            console.error("Error fetching product category breakdown:", err);
            throw err;
        }
    }
}

export default SalesFetchApi;
